import React, { useState, useEffect } from 'react';
import {
  View,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  TextInput,
  useWindowDimensions,
  Platform,
} from 'react-native';
import Text from '../../components/text';
import { useRoute } from '@react-navigation/native';
import ajax from '../../helpers/ajaxHelper';
import Urls from '../../services/Urls';
import drawerHoc from '../../components/drawerHOC/DrawerHOC';
import MarkAsComplete from '../../components/markAsComplete/MarkAsComplete';
import TextInputComponent from '../../components/textinput/TextInputComponent';
import DocumentView from '../../components/documentViewer';
import FilePicker from '../../components/filePicker/filepicker.web';
import { showErrorToast, showSuccesToast } from '../tenants/tenantOnboarding/helpers';
import { AntDesign, MaterialCommunityIcons, Ionicons } from '@expo/vector-icons';
import { SHADOWS, LAYOUT } from '../../constants/theme';
import { SetupSidebar, usePropertyRouteParams, PropertyBreadcrumb, PropertyHeaderBanner, PropertyStickyFooter } from './sharedComponents';

/* ─── Yes / No toggle ──────────────────────────────────────────── */
const YesNo = ({ value, onChange }) => (
  <View style={yn.row}>
    <TouchableOpacity
      style={[yn.btn, value === 'Yes' && yn.btnActive]}
      onPress={() => onChange('Yes')}
      activeOpacity={0.8}
    >
      <Text style={[yn.txt, value === 'Yes' && yn.txtActive]}>Yes</Text>
    </TouchableOpacity>
    <TouchableOpacity
      style={[yn.btn, value === 'No' && yn.btnActive]}
      onPress={() => onChange('No')}
      activeOpacity={0.8}
    >
      <Text style={[yn.txt, value === 'No' && yn.txtActive]}>No</Text>
    </TouchableOpacity>
  </View>
);
const yn = StyleSheet.create({
  row: { flexDirection: 'row', gap: 8 },
  btn: { paddingHorizontal: 24, paddingVertical: 10, borderRadius: 8, borderWidth: 1.5, borderColor: '#D1D5DB', backgroundColor: '#fff' },
  btnActive: { backgroundColor: '#6C2BD9', borderColor: '#6C2BD9' },
  txt: { fontSize: 14, fontWeight: '600', color: '#374151' },
  txtActive: { color: '#fff' },
});

/* ─── Yes / No / Not Applicable toggle ─────────────────────────── */
const YesNoNA = ({ value, onChange }) => {
  const opts = ['Yes', 'No', 'Not Applicable'];
  return (
    <View style={yna.row}>
      {opts.map((opt) => (
        <TouchableOpacity
          key={opt}
          style={[yna.btn, value === opt && yna.btnActive]}
          onPress={() => onChange(opt)}
          activeOpacity={0.8}
        >
          <Text style={[yna.txt, value === opt && yna.txtActive]}>{opt}</Text>
        </TouchableOpacity>
      ))}
    </View>
  );
};
const yna = StyleSheet.create({
  row: { flexDirection: 'row', gap: 8, flexWrap: 'wrap' },
  btn: { paddingHorizontal: 18, paddingVertical: 10, borderRadius: 8, borderWidth: 1.5, borderColor: '#D1D5DB', backgroundColor: '#fff' },
  btnActive: { backgroundColor: '#6C2BD9', borderColor: '#6C2BD9' },
  txt: { fontSize: 13, fontWeight: '600', color: '#374151' },
  txtActive: { color: '#fff' },
});

/* ─── Upload Drop Zone ──────────────────────────────────────────── */
const UploadZone = ({ onDataSuccess }) => (
  <View style={uz.wrap}>
    <View style={uz.iconWrap}>
      <Ionicons name="cloud-upload-outline" size={20} color="#6C2BD9" />
    </View>
    <FilePicker style={{ width: '100%' }} onDataSuccess={onDataSuccess} />
    <Text style={uz.hint}>
      Drag or <Text style={uz.link}>click to upload</Text>
    </Text>
  </View>
);
const uz = StyleSheet.create({
  wrap: { borderWidth: 1.5, borderStyle: 'dashed', borderColor: '#c4b5fd', borderRadius: 10, paddingVertical: 18, paddingHorizontal: 12, alignItems: 'center', backgroundColor: '#faf7ff' },
  iconWrap: { width: 36, height: 36, borderRadius: 18, backgroundColor: '#F0E8FF', alignItems: 'center', justifyContent: 'center', marginBottom: 4 },
  hint: { fontSize: 12, color: '#9CA3AF', marginTop: 4 },
  link: { color: '#6C2BD9', fontWeight: '600' },
});



/* ─── Licence Card ──────────────────────────────────────────────── */
const LicenceCard = ({
  isMobile,
  iconName,
  iconColor,
  iconBg,
  title,
  description,
  validBadge,
  question,
  useThreeWay,     // if true, show Yes/No/NA; else Yes/No
  value,
  onToggle,
  showFields,
  expiryDate,
  onExpiryChange,
  onUpload,
  documents,
}) => (
  <View style={[lc.card, isMobile && lc.cardMobile]}>
    {/* Header row */}
    <View style={lc.headerRow}>
      <View style={[lc.iconWrap, { backgroundColor: iconBg }]}>
        <MaterialCommunityIcons name={iconName} size={18} color={iconColor} />
      </View>
      <View style={{ flex: 1 }}>
        <Text style={lc.title}>{title}</Text>
        {description ? <Text style={lc.desc} numberOfLines={2}>{description}</Text> : null}
      </View>
      {validBadge && (
        <View style={lc.validBadge}>
          <Text style={lc.validText}>{validBadge}</Text>
        </View>
      )}
    </View>

    {/* Question + toggle */}
    <Text style={lc.question}>{question} <Text style={lc.star}>*</Text></Text>
    {useThreeWay
      ? <YesNoNA value={value} onChange={onToggle} />
      : <YesNo value={value} onChange={onToggle} />
    }

    {/* Expiry + Upload fields */}
    {showFields && (
      <View style={[lc.fields, isMobile && lc.fieldsMobile]}>
        <View style={isMobile ? lc.fieldFull : lc.fieldHalf}>
          <Text style={lc.fieldLabel}>Expiry Date</Text>
          <TextInputComponent
            onChangeText={onExpiryChange}
            isDate
            date={expiryDate}
          />
        </View>
        <View style={isMobile ? lc.fieldFull : lc.fieldHalf}>
          <Text style={lc.fieldLabel}>Upload Documents</Text>
          <UploadZone onDataSuccess={onUpload} />
        </View>
      </View>
    )}

    {/* Uploaded documents */}
    {documents}
  </View>
);
const lc = StyleSheet.create({
  card: { backgroundColor: '#fff', borderRadius: 14, padding: 18, marginBottom: 16, ...SHADOWS.sm },
  cardMobile: { padding: 14 },
  headerRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 12, marginBottom: 14 },
  iconWrap: { width: 40, height: 40, borderRadius: 10, alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  title: { fontSize: 14, fontWeight: '700', color: '#111827' },
  desc: { fontSize: 12, color: '#9CA3AF', marginTop: 2, lineHeight: 17 },
  validBadge: { backgroundColor: '#D1FAE5', borderRadius: 20, paddingHorizontal: 10, paddingVertical: 3, alignSelf: 'flex-start', flexShrink: 0 },
  validText: { fontSize: 11, fontWeight: '700', color: '#059669' },
  question: { fontSize: 13, fontWeight: '600', color: '#374151', marginBottom: 12, lineHeight: 20 },
  star: { color: '#EF4444' },
  fields: { flexDirection: 'row', gap: 16, marginTop: 16 },
  fieldsMobile: { flexDirection: 'column', gap: 16 },
  fieldHalf: { flex: 1, minWidth: 0 },
  fieldFull: { width: '100%', flexGrow: 0, flexShrink: 0, flexBasis: 'auto' },
  fieldLabel: { fontSize: 12, fontWeight: '600', color: '#374151', marginBottom: 6 },
});

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════ */
const Licensing = ({ navigation }) => {
  const routeParams = usePropertyRouteParams();
  const route = useRoute();
  const activePropertyId = routeParams.id;
  const propertyDisplayName = routeParams.name || 'Property';
  const { width } = useWindowDimensions();
  const isMobile = width <= 767;

  /* ── State (unchanged from original) ── */
  const [hmoLicense, setHMOLicense] = useState('');
  const [additionalLicense, setAdditional] = useState('');
  const [selectiveLicense, setSelective] = useState('');
  const [otherCertificate, setOther] = useState('');

  const [state, setState] = useState({
    hmo_license: [],
    hmo_license_expiry: '',
    additional_license: [],
    additional_license_expiry: '',
    selective_license: [],
    selective_license_expiry: '',
    other_license: [],
    other_license_expiry: '',
    other_license_title: '',
  });

  useEffect(() => {
    if (activePropertyId) _getLicensing(activePropertyId);
  }, [activePropertyId]);

  /* ── API calls (unchanged) ── */
  const _getLicensing = (id) => {
    ajax.get(Urls.Licensing + '/' + id)
      .then(({ data }) => {
        if (data.status) {
          setState({
            ...state,
            hmo_license: data?.data?.hmo_license?.files || [],
            hmo_license_expiry: data?.data?.hmo_license?.expiry_date || '',
            additional_license: data?.data?.additional_license?.files || [],
            additional_license_expiry: data?.data?.additional_license?.expiry_date || '',
            selective_license: data?.data?.selective_license?.files || [],
            selective_license_expiry: data?.data?.selective_license?.expiry_date || '',
            other_license: data?.data?.other_license?.files || [],
            other_license_expiry: data?.data?.other_license?.expiry_date || '',
            other_license_title: '',
          });
          if (data?.data?.hmo_license?.status) setHMOLicense(data?.data?.hmo_license?.status);
          if (data?.data?.additional_license?.status) setAdditional(data?.data?.additional_license?.status);
          if (data?.data?.selective_license?.status) setSelective(data?.data?.selective_license?.status);
        }
      })
      .catch((error) => console.log('error', error));
  };

  const _onSave = (navigatePage = true) => {
    if (hmoLicense === 'Yes' && !state?.hmo_license_expiry) {
      showErrorToast('HMO Licence expiry date is required'); return;
    }
    if (additionalLicense === 'Yes' && !state?.additional_license_expiry) {
      showErrorToast('Additional Licence expiry date is required'); return;
    }
    if (selectiveLicense === 'Yes' && !state?.selective_license_expiry) {
      showErrorToast('Selective Licence expiry date is required'); return;
    }
    if (state?.other_license_title && !state?.other_license_expiry) {
      showErrorToast('Other Licence expiry date is required'); return;
    }

    const requestBody = {
      hmo_license: { files: state?.hmo_license, expiry_date: state?.hmo_license_expiry || '', status: hmoLicense, name: 'hmo_license' },
      additional_license: { files: state?.additional_license, expiry_date: state?.additional_license_expiry || '', status: additionalLicense, name: 'additional_license' },
      selective_license: { files: state?.selective_license, expiry_date: state?.selective_license_expiry || '', status: selectiveLicense, name: 'selective_license' },
      other_license: { files: state?.other_license, expiry_date: state?.other_license_expiry || '', status: otherCertificate, title: state.other_license_title, name: 'other_license' },
    };
    ajax.post(Urls.Licensing + '/' + activePropertyId, requestBody)
      .then(({ data }) => {
        if (data.status) {
          if (navigatePage) navigation.navigate('PropertySection', { id: activePropertyId, name: propertyDisplayName });
        }
      })
      .catch((error) => console.log('error', error));
  };

  const _onAddCertifcate = (value, tag) => {
    let obj = {
      name: tag,
      document_url: value[0]?.openbrixName,
      expiry_date: new Date(),
      file_name: tag === 'other_license' ? state?.other_license_title : value[0]?.name,
    };
    setState({ ...state, [tag]: [...state[tag], obj], other_license_title: '', other_license_expiry: '' });
  };

  const moveToDocs = (data) => {
    ajax.post(Urls.MoveToDocs + '/' + activePropertyId, data)
      .then(({ data }) => {
        if (data.status) showSuccesToast('Document moved to myDocs section!');
        else showErrorToast('Something went wrong!');
      })
      .catch(() => showErrorToast('Something went wrong!'));
  };

  const _onRemove = (obj, tag, i, ismoveToDocs) => {
    let arr = state[tag];
    if (ismoveToDocs) {
      moveToDocs(arr[i]);
    } else {
      arr[i].is_deleted = true;
      setState({ ...state, [tag]: arr });
      _onSave(false);
    }
  };

  const GetBeautyName = (name) => {
    if (name && name.length > 0) return name.length > 15 ? name.substring(0, 15) : name;
  };

  /* ── Document lists ── */
  const renderDocs = (certArray, tag) =>
    certArray
      ?.filter((e) => !e?.is_deleted)
      .map((e, i) => (
        <DocumentView
          key={i}
          title={tag === 'hmo_license' ? GetBeautyName(e?.file_name) : e?.file_name}
          url={e?.document_url}
          onDelete={(moveDocs) => _onRemove(e, tag, i, moveDocs)}
        />
      ));

  /* ════════════════════════════
     MOBILE LAYOUT
  ════════════════════════════ */
  if (isMobile) {
    return (
      <View style={ms.root}>
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={ms.scroll}
          showsVerticalScrollIndicator={false}
        >
          {/* Header card */}
          <View style={ms.headerCard}>
            <View style={ms.headerIcon}>
              <MaterialCommunityIcons name="shield-outline" size={20} color="#6C2BD9" />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={ms.headerTitle}>Licensing</Text>
              <Text style={ms.headerSub}>Please fill in the licensing details for this property.</Text>
            </View>
          </View>

          {/* Mark as Complete */}
          <View style={ms.completeCard}>
            <MarkAsComplete
              label={'Licensing requirements'}
              flowID={route?.params?.id}
              flowtype={route?.params?.key}
              isComplete={route?.params?.status === 2}
            />
          </View>

          {/* HMO Licence */}
          <LicenceCard
            isMobile
            iconName="file-document-outline"
            iconColor="#6C2BD9"
            iconBg="#F0E8FF"
            title="HMO Licence"
            description="HMO licence required for properties with 5 or more tenants."
            validBadge="VALID"
            question="Do you have an in-date HMO Licence?"
            useThreeWay={false}
            value={hmoLicense}
            onToggle={setHMOLicense}
            showFields={hmoLicense === 'Yes'}
            expiryDate={state.hmo_license_expiry}
            onExpiryChange={(e) => setState({ ...state, hmo_license_expiry: e })}
            onUpload={(data) => _onAddCertifcate(data, 'hmo_license')}
            documents={renderDocs(state.hmo_license, 'hmo_license')}
          />

          {/* Additional Licence */}
          <LicenceCard
            isMobile
            iconName="lightning-bolt"
            iconColor="#F59E0B"
            iconBg="#FEF3C7"
            title="Additional Licence"
            description="Other local authority licences (e.g. selective, additional)."
            validBadge="VALID"
            question="Do you have an in-date Additional Licence?"
            useThreeWay={true}
            value={additionalLicense}
            onToggle={setAdditional}
            showFields={additionalLicense === 'Yes'}
            expiryDate={state.additional_license_expiry}
            onExpiryChange={(e) => setState({ ...state, additional_license_expiry: e })}
            onUpload={(data) => _onAddCertifcate(data, 'additional_license')}
            documents={renderDocs(state.additional_license, 'additional_license')}
          />

          {/* Selective Licence */}
          <LicenceCard
            isMobile
            iconName="file-star-outline"
            iconColor="#3B82F6"
            iconBg="#DBEAFE"
            title="Selective Licence"
            description="Required for certain properties in selective licensing areas."
            question="Do you have an in-date Selective Licence?"
            useThreeWay={true}
            value={selectiveLicense}
            onToggle={setSelective}
            showFields={selectiveLicense === 'Yes'}
            expiryDate={state.selective_license_expiry}
            onExpiryChange={(e) => setState({ ...state, selective_license_expiry: e })}
            onUpload={(data) => _onAddCertifcate(data, 'selective_license')}
            documents={renderDocs(state.selective_license, 'selective_license')}
          />

          {/* Other Licence */}
          <View style={ms.otherCard}>
            <Text style={ms.otherTitle}>Other Licence</Text>
            <Text style={ms.fieldLabel}>Licence title</Text>
            <TextInput
              style={ms.input}
              placeholder="Enter licence title"
              placeholderTextColor="#b0b0b0"
              value={state.other_license_title}
              onChangeText={(e) => setState({ ...state, other_license_title: e })}
              maxLength={50}
            />
            {state.other_license_title?.length > 0 && (
              <Text style={ms.charLeft}>{50 - state.other_license_title.length} characters left</Text>
            )}
            <Text style={[ms.fieldLabel, { marginTop: 14 }]}>Expiry Date</Text>
            <TextInputComponent
              onChangeText={(e) => setState({ ...state, other_license_expiry: e })}
              isDate
              date={state.other_license_expiry}
            />
            <Text style={[ms.fieldLabel, { marginTop: 14 }]}>Upload Documents</Text>
            <UploadZone onDataSuccess={(data) => _onAddCertifcate(data, 'other_license')} />
            {renderDocs(state.other_license, 'other_license')}
          </View>

          <View style={{ height: 100 }} />
        </ScrollView>

        <PropertyStickyFooter onSave={() => _onSave()} isMobile={isMobile} />
      </View>
    );
  }

  /* ════════════════════════════
     WEB / TABLET LAYOUT
  ════════════════════════════ */
  return (
    <View style={{ flex: 1, position: 'relative', backgroundColor: '#F5F7FB' }}>
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingBottom: 100 }}
        showsVerticalScrollIndicator={false}
      >
        {/* Breadcrumb */}
        <PropertyBreadcrumb
          navigation={navigation}
          propertyId={activePropertyId}
          propertyName={propertyDisplayName}
          currentStep="Licensing"
        />

        {/* Header Banner Card */}
        <PropertyHeaderBanner
          icon="document-text-outline"
          title="Licensing"
          subtitle="Please fill in the licensing details for this property."
          isMobile={isMobile}
          rightComponent={
            activePropertyId ? (
              <MarkAsComplete
                label={'Licensing requirements'}
                flowID={activePropertyId}
                flowtype={routeParams?.key}
                isComplete={routeParams?.status === 2}
              />
            ) : null
          }
        />

        {/* Body */}
        <View style={ws.body}>
          {/* Main card */}
          <View style={ws.mainCard}>
            {/* HMO Licence */}
            <LicenceCard
              isMobile={false}
              iconName="file-document-outline"
              iconColor="#6C2BD9"
              iconBg="#F0E8FF"
              title="HMO Licence"
              description="HMO licence required for properties with 5 or more tenants."
              validBadge="VALID"
              question="Do you have an in-date HMO Licence?"
              useThreeWay={false}
              value={hmoLicense}
              onToggle={setHMOLicense}
              showFields={hmoLicense === 'Yes'}
              expiryDate={state.hmo_license_expiry}
              onExpiryChange={(e) => setState({ ...state, hmo_license_expiry: e })}
              onUpload={(data) => _onAddCertifcate(data, 'hmo_license')}
              documents={renderDocs(state.hmo_license, 'hmo_license')}
            />

            {/* Additional Licence */}
            <LicenceCard
              isMobile={false}
              iconName="lightning-bolt"
              iconColor="#F59E0B"
              iconBg="#FEF3C7"
              title="Additional Licence"
              description="Other local authority licences (e.g. selective, additional)."
              validBadge="VALID"
              question="Do you have an in-date Additional Licence?"
              useThreeWay={true}
              value={additionalLicense}
              onToggle={setAdditional}
              showFields={additionalLicense === 'Yes'}
              expiryDate={state.additional_license_expiry}
              onExpiryChange={(e) => setState({ ...state, additional_license_expiry: e })}
              onUpload={(data) => _onAddCertifcate(data, 'additional_license')}
              documents={renderDocs(state.additional_license, 'additional_license')}
            />

            {/* Selective Licence */}
            <LicenceCard
              isMobile={false}
              iconName="file-star-outline"
              iconColor="#3B82F6"
              iconBg="#DBEAFE"
              title="Selective Licence"
              description="Required for certain properties in selective licensing areas."
              question="Do you have an in-date Selective Licence?"
              useThreeWay={true}
              value={selectiveLicense}
              onToggle={setSelective}
              showFields={selectiveLicense === 'Yes'}
              expiryDate={state.selective_license_expiry}
              onExpiryChange={(e) => setState({ ...state, selective_license_expiry: e })}
              onUpload={(data) => _onAddCertifcate(data, 'selective_license')}
              documents={renderDocs(state.selective_license, 'selective_license')}
            />

            {/* Other Licence section */}
            <View style={ws.otherSection}>
              <Text style={ws.otherTitle}>Other Licence</Text>
              <View style={ws.otherGrid}>
                <View style={{ flex: 1 }}>
                  <Text style={ws.fieldLabel}>Licence title</Text>
                  <TextInput
                    style={ws.input}
                    placeholder="Enter licence title"
                    placeholderTextColor="#b0b0b0"
                    value={state.other_license_title}
                    onChangeText={(e) => setState({ ...state, other_license_title: e })}
                    maxLength={50}
                  />
                  {state.other_license_title?.length > 0 && (
                    <Text style={ws.charLeft}>{50 - state.other_license_title.length} characters left</Text>
                  )}
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={ws.fieldLabel}>Expiry Date</Text>
                  <TextInputComponent
                    onChangeText={(e) => setState({ ...state, other_license_expiry: e })}
                    isDate
                    date={state.other_license_expiry}
                  />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={ws.fieldLabel}>Upload Documents</Text>
                  <UploadZone onDataSuccess={(data) => _onAddCertifcate(data, 'other_license')} />
                </View>
              </View>
              {renderDocs(state.other_license, 'other_license')}
            </View>
          </View>

          {/* Sidebar */}
          <SetupSidebar
            activeKey="licensing"
            propertyId={activePropertyId}
            propertyName={propertyDisplayName}
            navigation={navigation}
          />
        </View>
      </ScrollView>

      {/* Sticky footer */}
      <PropertyStickyFooter onSave={() => _onSave()} isMobile={isMobile} />
    </View>
  );
};

/* ═══════════════════════════════════════════════════════════════
   STYLES
═══════════════════════════════════════════════════════════════ */

/* ── MOBILE ── */
const ms = StyleSheet.create({
  root: { flex: 1, backgroundColor: '#F5F7FB' },
  scroll: { padding: 16, paddingBottom: 30 },

  progressCard: { backgroundColor: '#fff', borderRadius: 14, padding: 14, marginBottom: 12, ...SHADOWS.sm },
  progressRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 },
  progressTitle: { fontSize: 14, fontWeight: '700', color: '#111827' },
  progressStep: { fontSize: 12, color: '#6C2BD9', fontWeight: '700' },
  track: { height: 6, backgroundColor: '#F0E8FF', borderRadius: 3, overflow: 'hidden' },
  fill: { width: '75%', height: '100%', backgroundColor: '#6C2BD9', borderRadius: 3 },

  headerCard: { flexDirection: 'row', alignItems: 'flex-start', gap: 12, backgroundColor: '#fff', borderRadius: 14, padding: 16, marginBottom: 12, ...SHADOWS.sm },
  headerIcon: { width: 40, height: 40, borderRadius: 10, backgroundColor: '#F0E8FF', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  headerTitle: { fontSize: 17, fontWeight: '700', color: '#111827' },
  headerSub: { fontSize: 12, color: '#9CA3AF', marginTop: 3, lineHeight: 17 },

  completeCard: { backgroundColor: '#fff', borderRadius: 14, paddingHorizontal: 16, paddingVertical: 4, marginBottom: 12, ...SHADOWS.sm },

  otherCard: { backgroundColor: '#fff', borderRadius: 14, padding: 16, marginBottom: 16, ...SHADOWS.sm },
  otherTitle: { fontSize: 15, fontWeight: '700', color: '#111827', marginBottom: 14 },
  fieldLabel: { fontSize: 12, fontWeight: '600', color: '#374151', marginBottom: 6 },
  input: { borderWidth: 1, borderColor: '#D1D5DB', borderRadius: 8, paddingHorizontal: 14, paddingVertical: 10, fontSize: 14, color: '#111827', backgroundColor: '#fff' },
  charLeft: { fontSize: 11, color: '#9CA3AF', textAlign: 'right', marginTop: 4 },

  footer: { backgroundColor: '#fff', borderTopWidth: 1, borderTopColor: '#E5E7EB', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 14, ...SHADOWS.md },
  footerNote: { fontSize: 12, color: '#9CA3AF' },
  footerBtn: { backgroundColor: '#6C2BD9', borderRadius: 30, paddingHorizontal: 20, paddingVertical: 12 },
  footerBtnText: { color: '#fff', fontSize: 14, fontWeight: '700' },
});

/* ── WEB / TABLET ── */
const ws = StyleSheet.create({
  root: { flex: 1, backgroundColor: '#F5F7FB' },

  breadcrumbBar: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 24, paddingVertical: 14, flexWrap: 'wrap' },
  backBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, marginRight: 4 },
  backText: { fontSize: 13, color: '#6C2BD9', fontWeight: '600' },
  /* Banner Card */
  bannerCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    padding: 16,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
    gap: 12,
    ...SHADOWS.sm,
  },
  bannerCardMobile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  bannerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
    width: '100%',
  },
  bannerIconWrap: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: '#F5F3FF',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  bannerTextWrap: {
    flex: 1,
    minWidth: 0,
  },
  bannerTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#111827',
  },
  bannerSubtitle: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 2,
  },
  bannerRight: {},

  body: { flexDirection: 'row', alignItems: 'flex-start', gap: 20, paddingHorizontal: 24, paddingBottom: 100 },

  mainCard: { flex: 1, backgroundColor: '#fff', borderRadius: 16, padding: 24, ...SHADOWS.md },

  cardHeader: { flexDirection: 'row', alignItems: 'center', gap: 14, marginBottom: 24, paddingBottom: 20, borderBottomWidth: 1, borderBottomColor: '#F3F4F6', flexWrap: 'wrap' },
  cardIconWrap: { width: 44, height: 44, borderRadius: 12, backgroundColor: '#F0E8FF', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  cardTitle: { fontSize: 20, fontWeight: '700', color: '#111827' },
  cardSub: { fontSize: 13, color: '#9CA3AF', marginTop: 3 },

  otherSection: { marginTop: 8, paddingTop: 20, borderTopWidth: 1, borderTopColor: '#F3F4F6' },
  otherTitle: { fontSize: 16, fontWeight: '700', color: '#111827', marginBottom: 16 },
  otherGrid: { flexDirection: 'row', gap: 16, flexWrap: 'wrap' },
  fieldLabel: { fontSize: 12, fontWeight: '600', color: '#374151', marginBottom: 6 },
  input: { borderWidth: 1, borderColor: '#D1D5DB', borderRadius: 8, paddingHorizontal: 14, paddingVertical: 10, fontSize: 14, color: '#111827', backgroundColor: '#fff' },
  charLeft: { fontSize: 11, color: '#9CA3AF', textAlign: 'right', marginTop: 4 },

  stickyFooter: { position: 'absolute', bottom: 0, left: 0, right: 0, backgroundColor: '#fff', borderTopWidth: 1, borderTopColor: '#E5E7EB', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 24, paddingVertical: 14, zIndex: 1000, ...SHADOWS.md },
  footerNote: { fontSize: 13, color: '#9CA3AF' },
  footerBtn: { backgroundColor: '#6C2BD9', borderRadius: 30, paddingHorizontal: 24, paddingVertical: 12 },
  footerBtnText: { color: '#fff', fontSize: 14, fontWeight: '700' },
});

export default drawerHoc({
  Component: Licensing,
  showBackButton: true,
  noScroll: true,
});
